import { Button, HStack, Text, VStack } from '@chakra-ui/react';
import { Mic, Video } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { MediaRecorderPanel, type RecordingKind } from './MediaRecorder';
import { testIds } from './testIds';

export type SlideRecordingKind = 'AUDIO' | 'VIDEO';

const RECORDING_KINDS: Record<SlideRecordingKind, RecordingKind> = {
  AUDIO: 'audio',
  VIDEO: 'video',
};

const MAX_RECORDING_DURATION_MS: Record<RecordingKind, number> = {
  audio: 20 * 60 * 1000,
  video: 10 * 60 * 1000,
};

const RECORD_BUTTONS = {
  audio: { label: 'Record audio', Icon: Mic, testId: testIds.recordAudioButton },
  video: { label: 'Record video', Icon: Video, testId: testIds.recordVideoButton },
} satisfies Record<RecordingKind, { label: string; Icon: typeof Mic; testId: string }>;

/** The record buttons on a file upload slide, and the recorder they open. */
export function RecordResponse({
  recordingKinds,
  onSave,
  fileCount = 0,
  maxFiles,
  isDisabled = false,
  isInNativeApp = false,
  onUnsavedRecordingChange,
}: {
  recordingKinds: readonly SlideRecordingKind[];
  onSave?: (file: File) => Promise<void> | void;
  fileCount?: number;
  maxFiles?: number;
  isDisabled?: boolean;
  isInNativeApp?: boolean;
  onUnsavedRecordingChange?: (hasUnsavedRecording: boolean) => void;
}): ReactElement {
  const [recordingKind, setRecordingKind] = useState<RecordingKind | null>(null);
  const hasReachedFileLimit = maxFiles !== undefined && fileCount >= maxFiles;
  const fileLimitMessage = `You have added the maximum of ${maxFiles} files. Remove one to add another.`;

  return (
    <VStack w="full" spacing={4} alignItems="stretch">
      {recordingKind !== null && onSave ? (
        <MediaRecorderPanel
          kind={recordingKind}
          maxDurationMs={MAX_RECORDING_DURATION_MS[recordingKind]}
          onSave={onSave}
          onClose={() => setRecordingKind(null)}
          onUnsavedRecordingChange={onUnsavedRecordingChange}
          saveBlockedMessage={hasReachedFileLimit ? fileLimitMessage : undefined}
          isInNativeApp={isInNativeApp}
        />
      ) : (
        <VStack w="full" spacing={2} alignItems="flex-start">
          <HStack spacing={3} flexWrap="wrap">
            {recordingKinds.map((slideKind) => {
              const kind = RECORDING_KINDS[slideKind];
              const { label, Icon, testId } = RECORD_BUTTONS[kind];
              return (
                <Button
                  key={kind}
                  data-testid={testId}
                  variant="secondary"
                  leftIcon={<Icon size={iconSize.md} aria-hidden />}
                  isDisabled={isDisabled || hasReachedFileLimit}
                  onClick={() => setRecordingKind(kind)}
                >
                  {label}
                </Button>
              );
            })}
          </HStack>
          <Text fontSize="sm" color="fg.muted">
            {hasReachedFileLimit
              ? fileLimitMessage
              : 'You can play your recording back and record again as many times as you need before saving it.'}
          </Text>
        </VStack>
      )}
      <Text fontSize="sm" fontWeight="semibold" color="fg.default">
        Or upload a file
      </Text>
    </VStack>
  );
}
